// Errores del cobro (folio, pago de contado y enganche) → qué hace el vendedor. Los mensajes de
// inventario salen de la tabla única de `lineErrors`.
import { ApiError } from '@/api/errors';
import { formatMXN } from '@/shared/lib/format/money';
import { salePaymentErrorMessage } from './salePayments';
import { integrationFailure } from './integrationErrors';
import { lineErrorMessage } from './lineErrors';
import { PHONE_EXPIRED_MESSAGE } from './phoneReservation';
import {
    PROMOTION_NOT_REVALIDATED,
    PROMOTION_NOT_REVALIDATED_TOAST,
} from './promotionRevalidation';

/**
 * `product` = reagregar en Producto · `reload` = releer la venta · `retry` = reintentar aquí ·
 * `quote` = regresar a Cotización · `build-sale` = regresar a Arma la venta a revalidar la promo ·
 * `payment-method` = cambiar el medio de cobro o quitar la promo · `none` = solo avisar.
 */
export type ChargeRecovery =
    | 'product'
    | 'reload'
    | 'retry'
    | 'quote'
    | 'build-sale'
    | 'payment-method'
    | 'none';

export interface ChargeFailure {
    recovery: ChargeRecovery;
    message: string;
}

const RECOVERY_BY_CODE: Record<string, ChargeRecovery> = {
    CATALOG_UNAVAILABLE: 'retry',
    RESERVATION_EXPIRED: 'product',
    SESSION_CONCURRENT_UPDATE: 'reload',
};

/** Rechazo del cobro por medio cuando Promociones no da el nombre de la promo. */
export const PAYMENT_NOT_ELIGIBLE_MESSAGE =
    'Una promo de la venta no aplica con este medio de cobro. Cambia el medio de cobro o quita la promo.';

function amountMismatchMessage(error: ApiError): string {
    const expected = (error.data as { expected?: unknown } | undefined)?.expected;
    return typeof expected === 'number'
        ? `El monto a cobrar cambió a ${formatMXN(expected / 100)}. Vuelve a cargar la venta para ver el monto correcto.`
        : 'El monto a cobrar cambió. Vuelve a cargar la venta para ver el monto correcto.';
}

/** Salida de un error del cobro; `undefined` si el code no es de este contrato. */
export function chargeFailure(
    error: unknown,
    promotionCopy?: (error: unknown) => string | undefined,
): ChargeFailure | undefined {
    if (!(error instanceof ApiError)) return undefined;
    if (error.code === 'PROMOTION_PAYMENT_NOT_ELIGIBLE') {
        return {
            recovery: 'payment-method',
            message: promotionCopy?.(error) ?? PAYMENT_NOT_ELIGIBLE_MESSAGE,
        };
    }
    const detailMessage = salePaymentErrorMessage(error);
    if (detailMessage) return { recovery: 'none', message: detailMessage };
    if (error.code === 'SALE_PHONE_REQUIRED') {
        return { recovery: 'product', message: PHONE_EXPIRED_MESSAGE };
    }
    const integration = integrationFailure(error);
    if (integration && integration.recovery !== 'cash') {
        return { recovery: integration.recovery, message: integration.message };
    }
    if (error.code === PROMOTION_NOT_REVALIDATED) {
        return { recovery: 'build-sale', message: PROMOTION_NOT_REVALIDATED_TOAST };
    }
    if (error.code === 'PAYMENT_AMOUNT_MISMATCH') {
        return { recovery: 'reload', message: amountMismatchMessage(error) };
    }
    const recovery = RECOVERY_BY_CODE[error.code] as ChargeRecovery | undefined;
    const message = lineErrorMessage(error);
    return recovery && message ? { recovery, message } : undefined;
}
